import {
  Button,
  Menu,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowLeftRegular,
  ArrowUpload16Filled,
  ArrowUploadRegular,
  ChevronDoubleLeftRegular,
  MoreHorizontalRegular,
  PanelRightContractRegular,
  QuestionCircleRegular,
  SignOutRegular,
  WrenchScrewdriverRegular,
} from "@fluentui/react-icons";
import classNames from "classnames";
import { compareDesc } from "date-fns";
import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  ReactElement,
} from "react";

import { postSetPriorityVersion } from "@/api/versionCategory";
import { translation } from "@/i18n";
import { nsAccountMenu } from "@/i18n/locales/accountMenu";
import { nsOrg } from "@/i18n/locales/org";
import { nsSideNav } from "@/i18n/locales/sideNav";
import { nsVersion } from "@/i18n/locales/version";
import { useAppRouteId } from "../../../AppRoutes";
import { useAppRouteParams } from "../../../AppRoutes";
import {
  MAXIMUM_RESIZE_SIDE_MENU_WIDTH,
  MINIMUM_RESIZE_GLOBAL_SIDE_MENU_WIDTH,
} from "../../../const";
import { useContextMenuItemsVersionsMenu } from "../../../contextItems/hooks";
import { Version, checkIsPublishedByFileUpload } from "../../../domain/Version";
import { checkIsMfaSettingRequired } from "../../../helpers/checkIsMfaSettingRequired";
import { openHelpPage } from "../../../helpers/openHelpPage";
import { useResizeHandler } from "../../../hooks/useResizeHandler";
import optiumLogoWhite from "../../../images/optium_logo_white.svg";
import { buildOrgTopPath } from "../../../utils/orgTopPath";
import { useAppAccount } from "../../AppProvider/AppAccountProvider";
import { DialogPhaseType } from "../../Dialog/helpers/hooks";
import { LoadingCover, LoadingCoverWithoutText } from "../../LoadingCover";
import { SignOutConfirmDialog } from "../../SignOutConfirmDialog";
import { SortableTmpMenuSet, TmpMenuSetDivider } from "../helpers";

import { OrganizationAvatar } from "../../OrganizationAvatar";
import {
  OrganizationMenu,
  useHasOrganizationMenuContent,
} from "../../OrganizationMenu";
import { AppSideNavNameTooltip } from "../AppSideNavNameTooltip";
import { AppSideNavGlobalVersions } from "./AppSideNavGlobalVersions";
import { FileOptimizeDialog } from "./FileOptimizeDialog";

import styles from "./index.module.css";

import { useActiveFrontendAddons } from "@/components/AppProvider/AppFrontendAddonProvider";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import {
  useCachedOrganization,
  useIsTadsEnabled,
} from "@/components/InOrganizationProvider/OrganizationProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { useAvailableViewTypes } from "@/components/InOrganizationProvider/ViewTypesProvider";
import { parseDateTimeOrISODateTime } from "@/utils/dateFnsHelper";
import { AppSideNavGlobalContextProvider } from "../providers/AppSideNavGlobalContextProvider";
import { useAppSideNavOpenState } from "../providers/AppSideNavOpenStateProvider";

const useTranslation = translation.use(
  nsOrg,
  nsAccountMenu,
  nsSideNav,
  nsVersion
);

const SIDE_MENU_ICON_STYLE = { color: "var(--figma-color-sideMenuText)" };

/**
 * @description メニュ-画面左に常にあるメニュー.
 * バージョン画面や、トップページで、表示内容が変わる
 * 管理者モードが存在する
 * todo: 状態やCSSが散らかってきたので、整理したい
 * todo: AA developerも権限は管理者モードは開けなくする
 */
export const AppSideNavGlobal: React.FC<{ isAdminMode?: boolean }> = ({
  isAdminMode = false,
}) => {
  const { t } = useTranslation();
  const appRouteId = useAppRouteId();
  const { account } = useAppAccount();
  const { organizationId, extraPageId, versionId } = useAppRouteParams();
  const { versions, isLoadedVersions } = useVersions();
  const { me, hasPermission, hasPermissionWithId, isLoadedMe } =
    usePermissions();
  // TADS 一覧は、広域の read か、TADS 単位の read を 1 つでも持てば開ける (読める TADS だけが並ぶ)
  const hasAnyTadSheetReadPermission =
    hasPermission("tad_sheets", "read") ||
    (me?.permissions.tad_sheet.read.length ?? 0) > 0;
  const hasVersionCategoriesReadPermission = hasPermission(
    "version_categories",
    "read"
  );
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );

  const {
    versionCategories,
    setVersionCategories,
    isLoadedVersionCategories,
    runReloadVersionCategories,
  } = useVersionCategories();
  const { organization: memberOrganization } = useCachedOrganization();
  const { isTadsEnabled } = useIsTadsEnabled();
  const {
    organizations: appOrganizations,
    isLoadedOrganizations,
    orgServiceType,
  } = useAppOrgsAndInvitedOrgs();
  const appOrganization = appOrganizations.find(
    (org) => org.org_id === organizationId
  );
  const organization = memberOrganization || appOrganization;
  const { isAAAdminOrAbove, isAADeveloperOrAbove } = useAppAccount();
  const hasOrgMenuContent = useHasOrganizationMenuContent();

  const isMfaSettingRequired = checkIsMfaSettingRequired(account);

  const { showErrorNotification } = useAppNotificationManager();

  const sortedVersions = useMemo(
    () =>
      versions
        .filter((v) => !v.isDisabled)
        .sort((a, b) => {
          const _a = parseDateTimeOrISODateTime(a.publishedAt);
          const _b = parseDateTimeOrISODateTime(b.publishedAt);
          return _a && _b ? compareDesc(_a, _b) : 0;
        }),
    [versions]
  );
  const versionsToBeDisplayed = useMemo(
    () =>
      organization?.is_sheet_enabled
        ? sortedVersions
        : sortedVersions.filter((v) => checkIsPublishedByFileUpload(v)),
    [organization, sortedVersions]
  );

  // カテゴリごとにバージョンを分類
  const displayedVersionsMap: Map<string, Version[]> = useMemo(() => {
    if (!versionCategories) {
      return new Map();
    }
    const map = new Map(
      versionCategories?.map(({ id }) => [
        id,
        versionsToBeDisplayed.filter((v) => v.category === id),
      ])
    );
    // 付与されたカテゴリが取得されていない場合、もしくはカテゴリを付与されていない場合は「未分類」に入れる
    map.set(
      "unknown",
      versionsToBeDisplayed.filter(
        (v) => !versionCategories?.map(({ id }) => id).includes(v.category)
      )
    );
    return map;
  }, [
    organization,
    versions,
    versionCategories,
    hasVersionCategoriesReadPermission,
  ]);

  const handleChangePriorityVersion = useCallback(
    async (categoryId: string, versionId: string | null) => {
      const targetVersionCategory = versionCategories?.find(
        (cat) => cat.id === categoryId
      );
      if (!targetVersionCategory) return;
      const prevCategories = versionCategories;
      setVersionCategories((prev) => {
        if (!prev) return prev;
        return prev.map((cat) =>
          cat.id === categoryId
            ? { ...cat, priority_version_id: versionId }
            : cat
        );
      });
      try {
        await postSetPriorityVersion({
          versionCategoryId: categoryId,
          organizationId,
          versionId: versionId ?? undefined,
        });
      } catch (error) {
        setVersionCategories(prevCategories);
        runReloadVersionCategories();
        throw error;
      }
    },
    [
      organizationId,
      showErrorNotification,
      versionCategories,
      setVersionCategories,
      runReloadVersionCategories,
    ]
  );

  const {
    isGlobalOpen,
    isLocalOpen,
    isToggleable,
    globalWidth,
    setIsGlobalOpen,
    setIsLocalOpen,
    setGlobalWidth,
  } = useAppSideNavOpenState();
  const [isResizing, setIsResizing] = useState(false);

  /**
   * Dialog
   */
  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);
  const [isSignOutDialogVisible, setIsSignOutDialogVisible] = useState(false);

  /**
   * context
   */
  const [contextMenuType, setContextMenuType] = useState<
    "top" | "version" | null
  >(null);
  const versionContextMenuItems: ReactElement =
    useContextMenuItemsVersionsMenu();
  const { availableViewTypes } = useAvailableViewTypes();

  /**
   * @description メニュートップの、組織名やコンテキストメニュー表示部
   * 組織外ではロゴを表示する
   */
  const renderFirstMenu = useCallback(() => {
    if (!isLoadedOrganizations) {
      return <LoadingCover />;
    }
    const label = (
      <div
        className={classNames(styles.content, {
          [styles.contentStatic]: !hasOrgMenuContent,
        })}
        {...(hasOrgMenuContent ? { role: "button", tabIndex: 0 } : {})}
      >
        {!organizationId ? (
          <div className={styles.logo}>
            <img src={optiumLogoWhite} height={22} alt="OPTIUM" />
          </div>
        ) : (
          <>
            <OrganizationAvatar name={organization?.name ?? ""} size={24} />
            <div className={styles.textWithTooltip}>
              <AppSideNavNameTooltip
                content={organization?.name ?? ""}
                relationship="description"
              >
                <div className={styles.title}>{organization?.name}</div>
              </AppSideNavNameTooltip>
            </div>
          </>
        )}
        {hasOrgMenuContent && (
          <div className={styles.rightIcon}>
            <MoreHorizontalRegular />
          </div>
        )}
      </div>
    );
    return (
      <div className={styles.firstMenu}>
        {hasOrgMenuContent ? (
          <OrganizationMenu>{label}</OrganizationMenu>
        ) : (
          label
        )}
      </div>
    );
  }, [organization, isLoadedOrganizations, organizationId, hasOrgMenuContent]);

  /**
   * @description ダッシュボード、バージョン一覧のリンク
   */
  const renderSecondMenu = useCallback(() => {
    const body = (() => {
      /**
       * 組織外
       */
      if (!organizationId) {
        return null;
      } else {
        /**
         * 組織内
         */
        // 組織情報ロード中 or 権限がなければ何も表示しない
        if (
          !isLoadedOrganizations ||
          (!hasPermission("optimize", "write") &&
            !hasPermission("evaluate", "write"))
        )
          return null;
        return organization?.is_file_optimize_enabled ? (
          <>
            <TmpMenuSetDivider />
            <div className={styles.innerRootMenu}>
              <div style={{ padding: "0 15px" }}>
                <Button
                  appearance="secondary"
                  icon={<ArrowUploadRegular />}
                  style={{
                    borderRadius: "10px",
                    height: "40px",
                    padding: "0 24px",
                    width: "100%",
                    maxWidth: "230px",
                    backgroundColor: "#060521",
                    color: "#ebebeb",
                  }}
                  onClick={() => setDialogPhase("confirm")}
                >
                  {t.version.fileOptimize.menuButton()}
                </Button>
              </div>
            </div>
          </>
        ) : null;
      }
    })();
    return <>{body}</>;
  }, [t, organization, isLoadedOrganizations, organizationId, hasPermission]);
  const renderThirdMenu = useCallback(() => {
    const body = (() => {
      /**
       * 組織外
       */
      if (!organizationId) {
        return (
          <>
            <SortableTmpMenuSet
              id={"account"}
              text={t.accountMenu.item.accountSetting()}
              to={"/account_setting"}
              iconName="Contact"
              active={appRouteId === "accountSetting"}
              isDisableTooltip={true}
            />
            <SortableTmpMenuSet
              id={"organizations"}
              text={t.org.list.title()}
              to={"/organizations"}
              iconName="Org"
              active={appRouteId === "organizations"}
              isDisableTooltip={true}
            />
            <SortableTmpMenuSet
              id={"help"}
              text={t.accountMenu.item.help()}
              onClick={() => openHelpPage(orgServiceType)}
              iconElement={
                <QuestionCircleRegular style={SIDE_MENU_ICON_STYLE} />
              }
              isDisableTooltip={true}
            />
            <div className={styles.organizationDivider}>
              <TmpMenuSetDivider />
            </div>
            <SortableTmpMenuSet
              id={"logout"}
              text={t.accountMenu.item.signOut()}
              onClick={() => setIsSignOutDialogVisible(true)}
              iconElement={<SignOutRegular style={SIDE_MENU_ICON_STYLE} />}
              isDisableTooltip={true}
            />
            {isAAAdminOrAbove || isAADeveloperOrAbove ? (
              <>
                <div className={styles.organizationDivider}>
                  <TmpMenuSetDivider />
                </div>
                <SortableTmpMenuSet
                  id={"admin_view"}
                  text={t.accountMenu.adminGroup.adminPage()}
                  to={
                    isAAAdminOrAbove
                      ? "/admin/organizations"
                      : "/admin/accounts"
                  }
                  iconElement={
                    <WrenchScrewdriverRegular style={SIDE_MENU_ICON_STYLE} />
                  }
                  isDisableTooltip={true}
                />
              </>
            ) : null}
            {!isMfaSettingRequired && account?.default_org_id ? (
              <>
                <div className={styles.organizationDivider}>
                  <TmpMenuSetDivider />
                </div>
                <div>
                  <SortableTmpMenuSet
                    id={"go_to_org"}
                    text={t.sideNav.item.backToOrganization()}
                    to={buildOrgTopPath(
                      account.default_org_id,
                      appOrganizations.find(
                        (o) => o.org_id === account.default_org_id
                      )?.initial_extra_page_id
                    )}
                    iconElement={
                      <ArrowLeftRegular style={SIDE_MENU_ICON_STYLE} />
                    }
                    isDisableTooltip={true}
                  />
                </div>
              </>
            ) : null}
          </>
        );
      } else {
        /**
         * 組織内
         */
        // 組織情報ロード中は何も表示しない
        if (!isLoadedOrganizations) return null;
        return (
          <>
            {/* 導入初期のため TADS 本体の管理画面は aa:developer 以上にのみ開く */}
            {isTadsEnabled &&
            isAADeveloperOrAbove &&
            hasAnyTadSheetReadPermission ? (
              <AppSideNavNameTooltip
                content={t.sideNav.item.tadSheets()}
                relationship="description"
              >
                <span>
                  <SortableTmpMenuSet
                    id={"TadSheets"}
                    text={t.sideNav.item.tadSheets()}
                    to={
                      organizationId
                        ? `/organizations/${organizationId}/tad_sheets`
                        : ""
                    }
                    iconName="TadSheets"
                    active={
                      appRouteId === "tadSheets" ||
                      appRouteId === "tadSheetCreate" ||
                      appRouteId === "tadSheetSetting"
                    }
                    isDisableTooltip={true}
                  />
                </span>
              </AppSideNavNameTooltip>
            ) : null}
            <AppSideNavNameTooltip
              content={t.sideNav.item.versionList()}
              relationship="description"
            >
              <span>
                <SortableTmpMenuSet
                  id={"PageList"}
                  text={t.sideNav.item.versionList()}
                  to={organizationId ? `/organizations/${organizationId}` : ""}
                  iconName="PageList"
                  active={appRouteId === "organizationTop"}
                  isDisableTooltip={true}
                />
              </span>
            </AppSideNavNameTooltip>
            {/* ファイル最適化が有効かつシート機能が無効な場合を除き、バージョン比較を表示 */}
            {!(
              organization?.is_file_optimize_enabled &&
              !organization?.is_sheet_enabled
            ) && (
              <AppSideNavNameTooltip
                content={t.sideNav.item.versionComparison()}
                relationship="description"
              >
                <span>
                  <SortableTmpMenuSet
                    id={"VersionsSummary"}
                    text={t.sideNav.item.versionComparison()}
                    to={
                      organizationId
                        ? `/organizations/${organizationId}/versions_summary_comparison`
                        : ""
                    }
                    iconName="StackIndicator"
                    active={appRouteId === "versionsSummaryComparison"}
                    isDisableTooltip={true}
                  />
                </span>
              </AppSideNavNameTooltip>
            )}
            {availableViewTypes
              ?.filter((vt) => "type" in vt && vt.type === "extraPage")
              .sort((a, b) => a.view_type.localeCompare(b.view_type))
              .map((vt) => (
                <SortableTmpMenuSet
                  id={`extra_page_${vt.view_type}`}
                  key={`extra_page_${vt.view_type}`}
                  text={vt.name}
                  to={
                    organizationId
                      ? `/organizations/${organizationId}/extra_pages/${vt.view_type}`
                      : ""
                  }
                  iconName="PreviewLink"
                  active={
                    appRouteId === `extraPage` && extraPageId === vt.view_type
                  }
                />
              ))}
          </>
        );
      }
    })();
    return <div className={styles.innerRootMenu}>{body}</div>;
  }, [
    t,
    availableViewTypes,
    organization,
    isLoadedOrganizations,
    organizationId,
    appRouteId,
    isMfaSettingRequired,
    account,
    extraPageId,
    orgServiceType,
    isAAAdminOrAbove,
    isAADeveloperOrAbove,
    isTadsEnabled,
    hasPermission,
  ]);

  /**
   * @description バージョン一覧の文字列やデフォルト組織遷移リンクなど
   */
  const renderSupplement = useCallback(() => {
    /**
     * 組織外
     */
    if (!organizationId) {
      return null;
    } else {
      /**
       * 組織内
       */
      // 組織ロード中何も表示しない
      const isEnableButtonAction =
        organization?.is_sheet_enabled && hasVersionsMetaWritePermission;
      if (!isLoadedOrganizations) return null;
      if (!isEnableButtonAction) {
        return (
          <div
            className={classNames(
              styles.versionListName,
              styles.versionListNameDisabled
            )}
          >
            <div className={styles.label}>{t.sideNav.item.versions()}</div>
          </div>
        );
      }
      return (
        <Menu positioning={"after-bottom"}>
          <MenuTrigger>
            <div
              className={classNames(styles.versionListName, styles.hoverItem)}
              onMouseDown={() => {
                setContextMenuType((prev) => (!prev ? "version" : null));
              }}
              style={{
                ...(contextMenuType === "version"
                  ? { backgroundColor: "var(--figma-color-sideMenuHover)" }
                  : {}),
              }}
            >
              <div className={styles.label}>{t.sideNav.item.versions()}</div>
              <Add20Regular className={styles.versionAddButton} />
            </div>
          </MenuTrigger>
          <MenuPopover
            style={{
              width: "max-content",
              maxWidth: "none",
              whiteSpace: "nowrap",
            }}
          >
            {versionContextMenuItems}
          </MenuPopover>
        </Menu>
      );
    }
  }, [
    t,
    contextMenuType,
    isLoadedOrganizations,
    organizationId,
    organization,
    hasVersionsMetaWritePermission,
    versionContextMenuItems,
  ]);

  const renderInOrganizationMenu = useCallback(() => {
    if (!organizationId) return null;
    if (!isLoadedVersions || !isLoadedVersionCategories || !isLoadedMe) {
      return <LoadingCoverWithoutText />;
    }
    return versionCategories ? (
      Array.from(displayedVersionsMap).map(
        ([versionCategoryId, value], index, array) => (
          <AppSideNavGlobalVersions
            key={versionCategoryId}
            versionCategoryId={versionCategoryId}
            versions={value}
            versionCategories={versionCategories}
            onChangePriorityVersion={
              hasPermission("versions", "write") ||
              hasPermissionWithId(
                "versions_version_category",
                "write",
                versionCategoryId
              )
                ? handleChangePriorityVersion
                : undefined
            }
          />
        )
      )
    ) : (
      <AppSideNavGlobalVersions
        versionCategoryId="unknown"
        versions={versionsToBeDisplayed}
        versionCategories={versionCategories}
      />
    );
  }, [
    organizationId,
    isLoadedVersions,
    isLoadedVersionCategories,
    isLoadedMe,
    versionCategories,
    displayedVersionsMap,
    versionsToBeDisplayed,
    hasPermission,
    hasPermissionWithId,
    handleChangePriorityVersion,
  ]);

  const renderAdminMenu = useCallback(() => {
    return (
      <>
        <div className={styles.firstMenu}>
          <div
            className={classNames(styles.content, {
              [styles.isAdminContent]: isAdminMode,
            })}
          >
            ALGO ARTIS
          </div>
        </div>
        <TmpMenuSetDivider />
        <div className={styles.secondRoot}>
          <div className={styles.innerRootMenu}>
            {isAAAdminOrAbove && (
              <SortableTmpMenuSet
                id={"admin_organization"}
                to={"/admin/organizations"}
                text="組織"
                iconName={"Org"}
                iconColor="#000"
                isDisableTooltip={true}
                isAdminMode={isAdminMode}
              />
            )}
            <SortableTmpMenuSet
              id={"admin_accounts"}
              to={"/admin/accounts"}
              text="アカウント"
              iconName={"Contact"}
              iconColor="#000"
              isDisableTooltip={true}
              isAdminMode={isAdminMode}
            />
            {isAAAdminOrAbove && (
              <>
                <SortableTmpMenuSet
                  id={"admin_view"}
                  to={"/admin/viewmasters"}
                  text="ビューマスタ"
                  iconName={"AnalyticsView"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
                <SortableTmpMenuSet
                  id={"admin_frontend"}
                  to={"/admin/frontendmasters"}
                  text="フロントエンドアドオンマスタ"
                  iconName={"Puzzle"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
                <SortableTmpMenuSet
                  id={"admin_connect"}
                  to={"/admin/connect_masters"}
                  text="Connectマスタ"
                  iconName={"PlugConnected"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
                <SortableTmpMenuSet
                  id={"admin_endpoint_addon"}
                  to={"/admin/endpoint_addon_masters"}
                  text="Endpoint Addonマスタ"
                  iconName={"Server"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
                <SortableTmpMenuSet
                  id={"admin_permission_masters"}
                  to={"/admin/permission_masters"}
                  text="Permissionマスタ"
                  iconName={"Shield"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
                <SortableTmpMenuSet
                  id={"admin_ci_masters"}
                  to={"/admin/ci_masters"}
                  text="CIマスタ"
                  iconName={"Build"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
                <SortableTmpMenuSet
                  id={"admin_landing_bucket_folders"}
                  to={"/admin/landing_bucket_folders"}
                  text="データ連携バケット"
                  iconName={"OpenFolderHorizontal"}
                  iconColor="#000"
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
              </>
            )}
          </div>
          {!isMfaSettingRequired && account?.default_org_id ? (
            <>
              <TmpMenuSetDivider />
              <div className={styles.goToDefaulOrg}>
                <SortableTmpMenuSet
                  id={"admin_go_account"}
                  text="組織に戻る"
                  to={buildOrgTopPath(
                    account.default_org_id,
                    appOrganizations.find(
                      (o) => o.org_id === account.default_org_id
                    )?.initial_extra_page_id
                  )}
                  iconElement={<ArrowLeftRegular style={{ color: "#000" }} />}
                  isDisableTooltip={true}
                  isAdminMode={isAdminMode}
                />
              </div>
            </>
          ) : null}
        </div>
      </>
    );
  }, [isAdminMode, isMfaSettingRequired, account, isAAAdminOrAbove]);

  const handleCloseSelf = useCallback(() => {
    setIsGlobalOpen(false);
  }, [setIsGlobalOpen]);

  const handleOpenOther = useCallback(() => {
    setIsLocalOpen(true);
  }, [setIsLocalOpen]);

  const rootRef = useRef<HTMLDivElement>(null);
  const onResizeHandlerMouseDown = useResizeHandler({
    type: "right",
    targetRef: rootRef,
    onResizeStart: () => {
      setIsResizing(true);
    },
    onResize: (width) => {
      if (
        width <= MINIMUM_RESIZE_GLOBAL_SIDE_MENU_WIDTH ||
        width >= MAXIMUM_RESIZE_SIDE_MENU_WIDTH
      )
        return;
      setGlobalWidth(width);
    },
    onResizeEnd: () => {
      setIsResizing(false);
    },
  });

  const { isAllActiveAddonsLoaded } = useActiveFrontendAddons();

  if (!isLoadedMe && !isAdminMode && !!organizationId) {
    return null;
  }
  return (
    <div key={`${organization?.org_id}-nav-global`}>
      <AppSideNavGlobalContextProvider
        key={`appSideNavGlobalContext-${versionCategories?.length}`}
      >
        <aside
          role="navigation"
          aria-label={t.sideNav.global.ariaLabel()}
          aria-hidden={!isGlobalOpen}
          className={classNames(styles.root, {
            [styles.isResizing]: isResizing,
            [styles.isClosed]: !isGlobalOpen,
            [styles.isAdmin]: isAdminMode,
          })}
          style={{
            width: `${isGlobalOpen ? globalWidth : 0}px`,
          }}
          ref={rootRef}
        >
          {isAdminMode ? (
            renderAdminMenu()
          ) : (
            <>
              {renderFirstMenu()}
              {isAllActiveAddonsLoaded ? (
                <>
                  {renderSecondMenu()}
                  <TmpMenuSetDivider />
                  {renderThirdMenu()}
                  {/* 組織外 (アカウント設定) は以降のセクションが無いので区切りを出さない */}
                  {organizationId ? <TmpMenuSetDivider /> : null}
                  {renderSupplement()}
                  <div className={styles.secondRoot}>
                    {renderInOrganizationMenu()}
                  </div>
                  <TmpMenuSetDivider />
                  {isToggleable ? (
                    <div className={styles.thirdRoot}>
                      <Button
                        appearance="subtle"
                        icon={<ChevronDoubleLeftRegular />}
                        aria-label={t.sideNav.global.closeButtonAriaLabel()}
                        className={classNames(
                          styles.footerButton,
                          styles.footerButtonExpand
                        )}
                        onClick={handleCloseSelf}
                      />
                      {!isLocalOpen && versionId ? (
                        <Button
                          appearance="subtle"
                          icon={<PanelRightContractRegular />}
                          aria-label={t.sideNav.global.openLocalButtonAriaLabel()}
                          className={styles.footerButton}
                          onClick={handleOpenOther}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <TmpMenuSetDivider />
                  <p>{t.sideNav.addonLoadError()}</p>
                </>
              )}
            </>
          )}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={globalWidth}
            aria-valuemin={MINIMUM_RESIZE_GLOBAL_SIDE_MENU_WIDTH}
            aria-valuemax={MAXIMUM_RESIZE_SIDE_MENU_WIDTH}
            className={styles.rootResizeHandler}
            onMouseDown={() => {
              if (!isGlobalOpen) return;
              onResizeHandlerMouseDown();
            }}
          />
        </aside>
        <FileOptimizeDialog
          dialogPhase={dialogPhase}
          setDialogPhase={setDialogPhase}
        />
        <SignOutConfirmDialog
          open={isSignOutDialogVisible}
          onClose={() => setIsSignOutDialogVisible(false)}
        />
      </AppSideNavGlobalContextProvider>
    </div>
  );
};
